// as rgb values, to allow changing opacity and manipulate better
export const color = {
  'P-00': '255 255 255',
  'P-20': '237 229 252',
  'P-40': '210 185 255',
  'P-60': '147 109 216',
  'P-80': '95 30 204',
  'P-100': '53 5 132',
  'N-10': '248 248 248',
  'N-20': '238 238 238',
  'N-40': '228 228 228',
  'N-60': '158 158 158',
  'N-80': '100 100 100',
  'N-90': '36 36 36',
  'N-100': '19 19 19',
  'ERROR-100': '218 0 0',
  'ERROR-20': '255 226 226',
  'SUCCESS-100': '10 204 47',
  'SUCCESS-80': '4 122 46',
  'WARNING-900': '255 179 64',
  'EX-100': '255 233 195',
  'CR-100': '204 205 242',
  'D-100': '204 124 30',
  'D-60': '216 191 163',
  'D-40': '245 229 210',
  'D-20': '255 215 168',
  'G-100': '75 229 137',
  'G-80': '109 194 125',
  'G-60': '160 212 170',
  'G-40': '200 239 207',
  'B-100': '0 0 0',
  'B-60': '64 64 64',
  'W-100': '255 255 255',
  'B-light': '229 246 253',
  'B-standard': '68 138 247',
  'Secondary-200': '227 229 238',
  'Secondary-900': '44 53 72',
  inherit: 'inherit',
};

export const rgb = (v: keyof typeof color) => `rgb(${color[v]})`;

export const colorOpacity = {
  'B-100/50': 0.5,
  'W-100/50': 0.5,
  'W-100/15': 0.15,
  'W-100/30': 0.3,
  'N-80/15': 0.15,
};

export const space = {
  '0': '0px',
  '1': '1px',
  xs: '2px',
  s: '4px',
  m: '8px',
  l: '16px',
  xl: '24px',
  '2xl': '32px',
  '3xl': '48px',
  '4xl': '56px',
  '5xl': '64px',
} as const;

export const spaceExtended = {
  ...space,
  auto: 'auto',
};

export const clipPath = {
  sm: 'xywh(0px 0px 100% 100% round 4px)',
  m: 'xywh(0px 0px 100% 100% round 10px)',
};

export const positions = {
  initial: 'initial',
  '0': '0px',
  xs: '2px',
  s: '4px',
  m: '8px',
  l: '16px',
  xl: '24px',
  '-xs': '-2px',
  '-s': '-4px',
  '-m': '-8px',
  '-l': '-16px',
  '-xl': '-24px',
  '1/2': '50%',
};

export const borderRadius = {
  none: 'none',
  xs: '4px',
  s: '8px',
  m: '10px',
  l: '20px',
  xl: '30px',
  full: '100%',
} as const;

export const fontSize = {
  xxs: '10px',
  xs: '12px',
  s: '14px',
  m: '16px',
  l: '20px',
  xl: '24px',
  '2xl': '32px',
  '3xl': '36px',
  '4xl': '50px',
  '5xl': '57px',
};

export const lineHeight = {
  xs: '16px',
  s: '20px',
  m: '24px',
  l: '26px',
  xl: '32px',
  '2xl': '40px',
  '3xl': '44px',
  '4xl': '58px',
  '5xl': '64px',
};

export const letterSpacing = {
  xs: '1px',
};

export const fontWeight = {
  light: '300',
  normal: '400',
  medium: '500',
  semiBold: '600',
  bold: '700',
  extraBold: '800',
};

export const fontFamily = {
  visuelt: 'Visuelt, sans-serif',
  caveat: 'Caveat',
};

export const borderWidth = {
  xs: '1px',
  s: '2px',
};

export const size = [
  '100%',
  '100vh',
  '100dvh',
  '100vw',
  'auto',
  'fit-content',
  'min-content',
  'max-content',
  'inherit',
];

export const overflow = ['hidden', 'auto', 'scroll'] as const;

export const boxShadowLight = {
  1: '0px 1px 3px 1px rgba(0, 0, 0, 0.15), 0px 1px 2px 0px rgba(0, 0, 0, 0.3)',
  2: '0px 2px 6px 2px rgba(100, 100, 100, 0.15), 0px 1px 2px 0px rgba(100, 100, 100, 0.3)',
  3: '0px 1px 3px 0px rgba(100, 100, 100, 0.3), 0px 4px 8px 3px rgba(100, 100, 100, 0.15),',
  4: '0px 2px 3px 0px rgba(100, 100, 100, 0.3), 0px 6px 10px 4px rgba(100, 100, 100, 0.15)',
  5: '0px 4px 4px 0px rgba(100, 100, 100, 0.3), 0px 8px 12px 6px rgba(100, 100, 100, 0.15)',
  8: '0px 6px 12px 0px rgba(29, 41, 57, 0.08), 0px 0px 1px 0px rgba(234, 236, 240, 1)',
  // x: '0px 4px 4px 0px rgba(100, 100, 100, 0.30), 0px 8px 12px 6px rgba(100, 100, 100, 0.15);',
};

export const breakpoints = {
  mobile: { max: 767 },
  tablet: { min: 768, max: 1279 },
  desktop: { min: 1280 },
  largeDesktop: { min: 1400 },
};

export type Breakpoints = keyof typeof breakpoints;

export const breakpointsMediaQueries = {
  mobile: `screen and (max-width: ${breakpoints.mobile.max}px)`,
  tablet: `screen and (min-width: ${breakpoints.tablet.min}px) and (max-width: ${breakpoints.tablet.max}px)`,
  desktop: `screen and (min-width: ${breakpoints.desktop.min}px)`,
  largeDesktop: `screen and (min-width: ${breakpoints.largeDesktop.min}px)`,
};
